import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** The api's code for a body that failed schema validation. */
export const VALIDATION_FAILED_CODE = 'validation_failed';

export interface MappedServerErrors {
  /** Attach to the named control with `setError`. */
  fieldErrors: { path: string; message: string }[];
  /** Show above the form — nothing on screen owns these. */
  formErrors: string[];
}

/**
 * Turns the api's rejection into something the form can display.
 *
 * The rule that matters is the **fallback**: a `details` key naming a path this
 * form does not render is surfaced at form level rather than dropped. Silently
 * discarding it produces the worst failure a form has — Save does nothing, no
 * field is marked, and the operator concludes the button is broken. That happens
 * for real whenever the api validates something the form does not show: a
 * derived field, a rule on the whole object, a locale the form has not built a
 * tab for yet.
 *
 * The test is **`details`, not the code**. Every domain error may carry field
 * paths, and `validation_failed` — the request pipe's code — is only the most
 * common one: a settings write is refused with `invalid_setting_value`, whose
 * details name the property that was wrong, and gating on the code dropped them
 * on the floor and reported one form-level line instead (found by the P10.3b
 * verifier round, after the phase had shipped a re-keying util with no consumer).
 * An error that names no field is still one form-level message: a 409 "that key
 * already exists" belongs above the form, not pinned to a field the server never
 * named.
 */
export function mapServerErrors(
  error: ApiError | undefined | null,
  knownPaths: readonly string[],
): MappedServerErrors {
  if (error === undefined || error === null) {
    return { fieldErrors: [], formErrors: [] };
  }

  if (error.details === undefined) {
    return { fieldErrors: [], formErrors: error.message === '' ? [] : [error.message] };
  }

  const known = new Set(knownPaths);
  const fieldErrors: { path: string; message: string }[] = [];
  const formErrors: string[] = [];

  for (const [path, messages] of Object.entries(error.details)) {
    // The api sends every message for a path; the form shows the first, because
    // "must be at least 2 characters" and "must be lower_snake_case" stacked on
    // one input is noise. The rest are still in the payload for the log.
    const message = messages[0] ?? '';

    if (message === '') {
      continue;
    }

    if (known.has(path)) {
      fieldErrors.push({ path, message });
      continue;
    }

    // Named, not bare: an operator reporting "translations.en.name: Required"
    // gives support something to work with, where a lone "Required" does not.
    formErrors.push(`${path}: ${message}`);
  }

  // `details` that produced nothing — every message empty — must not turn the
  // refusal into silence. Save would appear to do nothing with no error
  // anywhere, which is the worst outcome this function exists to prevent, and
  // it is the one the code test used to hide: before the widening a non-
  // validation error in this state still showed its own message.
  if (fieldErrors.length === 0 && formErrors.length === 0 && error.message !== '') {
    return { fieldErrors, formErrors: [error.message] };
  }

  return { fieldErrors, formErrors };
}
